import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { crud } from '@/common/crud';
import { Encounter, EpisodeOfCare, CareTeam, Reference } from '@/fhirworks';
import { v4 as uuidv4 } from 'uuid';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getLocationPosCode } from '@/common/workflowEngine/useEncounterFlow';
import { useOutcomeToolsPatientSync } from '@/composables/useOutcomeToolsPatientSync';

const { postTransaction } = useWorkflow();

// OutcomeTools V2 after-save syncs, fired only when this flow posted its own
// transaction — when composed into a caller's transaction, the caller's
// trigger covers it. Fire-and-forget: no-op on non-V2 accounts, the server
// skips unlinked patients, and a failure toasts without touching the save.
const syncEncounterToOutcomeTools = (encounter) => {
    const patientId = encounter?.subject?.id;
    if (patientId && encounter?.id) {
        useOutcomeToolsPatientSync().syncEncounter(patientId, encounter.id);
    }
};
const syncEpisodeToOutcomeTools = (episodeOfCare) => {
    const patientId = episodeOfCare?.patient?.id;
    if (patientId && episodeOfCare?.id) {
        useOutcomeToolsPatientSync().syncEpisode(patientId, episodeOfCare.id);
    }
};
// OT has no enrollment delete — this pauses the orphaned enrollment's surveys.
const syncDeletedEncounterToOutcomeTools = (patientId, encounterId) => {
    if (patientId && encounterId) {
        useOutcomeToolsPatientSync().syncEncounterDeleted(patientId, encounterId);
    }
};

const createEpisodeOfCare = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const episodeOfCare =
        data.episodeOfCare instanceof EpisodeOfCare
            ? data.episodeOfCare
            : new EpisodeOfCare({
                  id: uuidv4(),
                  status: data.status ? data.status : 'planned',
                  statusHistory: [
                      {
                          period: { start: new Date().toISOString() },
                          status: data.status ? data.status : 'planned',
                          bnStatus: data?.eligibilityStatus,
                      },
                  ],
                  patient: { id: data.patient.id, resourceType: 'Patient' },
                  placementStatus: data.placementStatus,
                  statusReason: data.statusReason,
                  complaint: data.complaint,
                  encounter: data.encounter,
                  location: data.location,
                  managingOrganization: data.managingOrganization,
                  period: { start: data.period?.start },
                  estPeriod: { start: data.estPeriod?.start },
                  eligibilityStatus: data.eligibilityStatus,
                  dischargeRationale: data.dischargeRationale,
                  externalReferral: data.externalReferral,
              });

    if (data.complaint) {
        episodeOfCare.originalComplaint = data.complaint;
    }
    transaction.push(crud.post(episodeOfCare));

    transaction = await addEncounter(
        {
            episodeOfCare: episodeOfCare,
            program: data.program,
            location: data.location,
            additionalEncounterInfo: data?.additionalEncounterInfo,
            careTeam: data.careTeam,
        },
        transaction,
    );

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        if (result) syncEpisodeToOutcomeTools(episodeOfCare);
        return result;
    }

    return transaction;
};

const deleteEpisodeOfCare = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // delete Encounters and all related resources (CareTeam)
    const deletedEncounterIds = [];
    const resources = await $httpFhirApi.get('/Encounter?episode-of-care=' + data.episodeOfCare.id + '&_revinclude=CareTeam:encounter');
    resources.data.forEach((item) => {
        if (['Encounter', 'CareTeam'].includes(item.resourceType)) {
            if (item.resourceType === 'Encounter') {
                deletedEncounterIds.push(item.id);
            }
            transaction.push(crud.delete(item));
        }
    });
    // delete Primary CareTeam
    transaction.push(crud.delete(data.episodeOfCare.team[0]));
    // delete the EpisodeOfCare
    transaction.push(crud.delete(data.episodeOfCare));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        if (result) deletedEncounterIds.forEach((encounterId) => syncDeletedEncounterToOutcomeTools(data.episodeOfCare.patient?.id, encounterId));
        return result;
    }

    return transaction;
};

const addEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const episodeOfCare = data.episodeOfCare instanceof EpisodeOfCare ? data.episodeOfCare : new EpisodeOfCare(data.episodeOfCare);

    let additionalEncounterInfoStatus = data.additionalEncounterInfo?.status;
    let additionalEncounterInfoEligibility = data.additionalEncounterInfo?.eligibility;
    let additionalEncounterInfoStartDate = data.additionalEncounterInfo?.startDate;
    let additionalEncounterInfoEstimatedStartDate = data.additionalEncounterInfo?.estimatedStartDate;
    let additionalEncounterInfoStatusReason = data.additionalEncounterInfo?.statusReason;

    let status = additionalEncounterInfoStatus ? additionalEncounterInfoStatus : syncEpisodeToEncounterStatus(episodeOfCare.status || 'planned');
    const encounterSetting = data.encounter instanceof Encounter ? undefined : await getLocationPosCode(data.location?.id);

    // create Encounter: if already defined just use it
    const encounter =
        data.encounter instanceof Encounter
            ? data.encounter
            : // this is primarily used when creating a new Patient with an initial EpisodeOfCare/Program
              new Encounter({
                  id: uuidv4(),
                  class: { code: 'AMB' },
                  type: [{ text: 'PROGRAM' }],
                  episodeOfCare: [{ id: episodeOfCare.id, resourceType: 'EpisodeOfCare' }],
                  subject: episodeOfCare.patient,
                  healthcareService: data.program,
                  serviceProvider: episodeOfCare.managingOrganization,
                  location: [{ location: { id: data.location.id, resourceType: 'Location' } }],
                  setting: encounterSetting,
                  status: status,
                  statusHistory: [{ status: status, period: { start: new Date().toISOString(), bnStatus: additionalEncounterInfoEligibility } }],
              });

    // Add additional encounter properties if found in data object
    if (additionalEncounterInfoEligibility) {
        encounter.eligibilityStatus = additionalEncounterInfoEligibility;
    }
    if (additionalEncounterInfoStartDate) {
        encounter.period = { start: additionalEncounterInfoStartDate };
    }
    if (additionalEncounterInfoEstimatedStartDate) {
        encounter.estPeriod = { start: additionalEncounterInfoEstimatedStartDate };
    }
    if (additionalEncounterInfoStatusReason) {
        encounter.statusReason = additionalEncounterInfoStatusReason;
    }

    transaction.push(crud.post(encounter));

    transaction = await addPrimaryCareTeam(
        {
            episodeOfCare: episodeOfCare,
            careTeam: data.careTeam || {},
            encounter: encounter,
        },
        transaction,
    );

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        if (result) syncEncounterToOutcomeTools(encounter);
        return result;
    }

    return transaction;
};

const syncEpisodeToEncounterStatus = function (eocStatus) {
    switch (eocStatus) {
        case 'active':
            eocStatus = 'in-progress';
            break;
        case 'onhold':
            eocStatus = 'onleave';
            break;
        case 'waitlist':
            eocStatus = 'planned';
            break;
    }
    return eocStatus;
};
const updateEpisodeOfCare = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    transaction.push(crud.patch(data.episodeOfCare));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        if (result) syncEpisodeToOutcomeTools(data.episodeOfCare);
        return result;
    }

    return transaction;
};

const updateEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    transaction.push(crud.patch(data.encounter));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        // Only sync when the transaction actually saved — postTransaction
        // returns null on failure, and syncing then would push stale data
        // (or, for deletes, pause OT surveys for a record that still exists).
        if (result) syncEncounterToOutcomeTools(data.encounter);
        return result;
    }

    return transaction;
};

const deleteEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // delete Encounter
    transaction.push(crud.delete(data.encounter));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        if (result) syncDeletedEncounterToOutcomeTools(data.encounter?.subject?.id, data.encounter?.id);
        return result;
    }

    return transaction;
};

const intakeEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const timeStamp = new Date().toISOString();
    // update Encounter
    // Must clear value in eligibilityStart prop when setting to in-progress
    // Looked at checking the value of the status but it has already been changed
    // so there is no use in doing that
    data.encounter.status = 'in-progress';
    data.encounter.eligibilityStatus = '';
    data.encounter.statusHistory.push({ status: 'in-progress', period: { start: timeStamp } });

    // Don't overwrite period start date if one already exist
    if (!data.encounter.period?.start) {
        data.encounter.period = { start: timeStamp };
    }
    transaction.push(crud.patch(data.encounter));

    // set EpisodeOfCare to 'active' and set start date if blank
    let episodeOfCare = data.episodeOfCare;
    if (!episodeOfCare) {
        const eoc = await $httpFhirApi.get('/EpisodeOfCare?id=' + data.encounter.episodeOfCare.id);
        episodeOfCare = new EpisodeOfCare(eoc.data[0]);
    }
    // If status has been changed add entry into statusHistory array
    let orgEOCStatus = episodeOfCare.status;
    episodeOfCare.status = 'active';
    if (orgEOCStatus !== episodeOfCare.status) {
        episodeOfCare.statusHistory = {
            status: 'active',
            period: { start: new Date().toISOString() },
        };
    }
    if (!episodeOfCare.period?.start) {
        episodeOfCare.period = { start: timeStamp, end: episodeOfCare.period?.end };
    }
    transaction.push(crud.patch(episodeOfCare));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        // Only sync when the transaction actually saved — postTransaction
        // returns null on failure, and syncing then would push stale data
        // (or, for deletes, pause OT surveys for a record that still exists).
        if (result) syncEncounterToOutcomeTools(data.encounter);
        return result;
    }

    return transaction;
};

const dischargeEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const timeStamp = new Date().toISOString();
    // update Encounter
    data.encounter.status = 'finished';
    data.encounter.statusHistory.push({ status: 'finished', period: { start: timeStamp } });
    data.encounter.period = { start: data.encounter.period?.start, end: timeStamp };
    transaction.push(crud.patch(data.encounter));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        // Only sync when the transaction actually saved — postTransaction
        // returns null on failure, and syncing then would push stale data
        // (or, for deletes, pause OT surveys for a record that still exists).
        if (result) syncEncounterToOutcomeTools(data.encounter);
        return result;
    }

    return transaction;
};

const dischargeTransferEncounter = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    const timeStamp = data.sourceEncounter.period.end || new Date().toISOString();
    // update the source encounter
    data.sourceEncounter.status = 'finished';
    data.sourceEncounter.statusHistory.push({ status: 'finished', period: { start: timeStamp } });
    data.sourceEncounter.period = { start: data.sourceEncounter.period.start, end: timeStamp };
    data.sourceEncounter.transferDestination = new Reference({ id: data.encounter.id, resourceType: 'Encounter' });
    transaction.push(crud.patch(data.sourceEncounter));

    // Create new encounter and intake if required
    if (data.encounter.temp?.intakeNow) {
        data.encounter.status = 'in-progress';
        data.encounter.statusHistory.push({ status: 'in-progress', period: { start: timeStamp } });
        data.encounter.period = { start: timeStamp };
    }
    data.encounter.transferOrigin = new Reference({ id: data.sourceEncounter.id, resourceType: 'Encounter' });
    transaction.push(crud.post(data.encounter));

    if (existingTransaction === false) {
        const result = await postTransaction(transaction);
        if (result) {
            syncEncounterToOutcomeTools(data.sourceEncounter);
            syncEncounterToOutcomeTools(data.encounter);
        }
        return result;
    }

    return transaction;
};

const addPrimaryCareTeam = async (data, existingTransaction = false) => {
    let transaction = existingTransaction || [];

    // if fully qualified CareTeam then use it otherwise create a new one
    const careTeam = data.careTeam?.id
        ? data.careTeam
        : new CareTeam({
              id: uuidv4(),
              ...data.careTeam,
              encounter: { id: data.encounter.id, resourceType: 'Encounter' },
          });
    transaction.push(crud.post(careTeam));
    // add CareTeam to EpisodeOfCare
    const episodeOfCare = data.episodeOfCare instanceof EpisodeOfCare ? data.episodeOfCare : new EpisodeOfCare(data.episodeOfCare);
    episodeOfCare.team[0] = { id: careTeam.id, resourceType: 'CareTeam' };
    transaction.push(crud.patch(episodeOfCare));

    if (existingTransaction === false) {
        return await postTransaction(transaction);
    }

    return transaction;
};

export function useEpisodeOfCareFlow() {
    return {
        createEpisodeOfCare,
        deleteEpisodeOfCare,
        updateEpisodeOfCare,
        addEncounter,
        updateEncounter,
        deleteEncounter,
        intakeEncounter,
        dischargeEncounter,
        dischargeTransferEncounter,
    };
}
